/* ==========================================================================
   FNDRY — Find your routine (quiz)
   Plain JS, no framework. Stepped flow over real radio groups, scoring,
   recommendation, quick add to cart.

   Progressive enhancement: the markup ships with the fallback panel visible
   and the form inert. This script sets [data-fq-ready] on the section root,
   which is what reveals the flow. If it throws, [data-fq-failed] puts the
   fallback back — the page is never left holding nothing.
   ========================================================================== */

(function () {
  'use strict';

  var ROOT = '[data-fndry-quiz]';

  /* Tie-break order — first key listed wins an equal score.

     `menopause` leads: someone who named the change itself gets the product
     built for it rather than a skin product on a coin toss. `duo` sits above
     its own two halves, so a tie between the pair and one half resolves to
     the pair that covers both signals. `astaxanthin` is last — it is the
     unmarketed SKU and should only ever win outright. */
  var KEYS = ['menopause', 'duo', 'collagen', 'ceramides', 'astaxanthin'];

  /* ---------- helpers -------------------------------------------------- */

  function $(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function $$(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* --fndry-header-h may be authored in px or rem, and the root is 10px. */
  function cssLength(value, fallback) {
    var raw = String(value || '').trim();
    var n = parseFloat(raw);
    if (isNaN(n)) return fallback;
    if (raw.indexOf('rem') > -1) {
      return n * (parseFloat(getComputedStyle(document.documentElement).fontSize) || 10);
    }
    return n;
  }

  function headerOffset() {
    var styles = getComputedStyle(document.documentElement);
    return cssLength(styles.getPropertyValue('--fndry-header-h'), 68) + 24;
  }

  /* ---------- scoring --------------------------------------------------- */

  /* Each answered option contributes its question's weight to one outcome
     key. An option wired to 'none' (a decline-to-answer) contributes nothing.

     Skin Duo is largely a derived result rather than a directly chosen one:
     where both halves of the pair surface across the answers, the bundle
     answers what either half only half-answers, so it takes a bonus equal to
     the weaker half. That keeps it out of the running when only one half was
     asked for — three firmness answers still resolve to collagen alone. */
  function resolve(scores, fallbackKey) {
    var total = 0;
    var tally = {};

    KEYS.forEach(function (key) {
      tally[key] = scores[key] || 0;
      total += tally[key];
    });
    if (total === 0) return fallbackKey;

    if (tally.collagen > 0 && tally.ceramides > 0) {
      tally.duo += Math.min(tally.collagen, tally.ceramides);
    }

    var best = KEYS[0];
    var bestScore = -1;
    KEYS.forEach(function (key) {
      if (tally[key] > bestScore) {
        bestScore = tally[key];
        best = key;
      }
    });
    return best;
  }

  /* ---------- cart ------------------------------------------------------ */
  /* Added state is derived from /cart.js, not from the click — so it survives
     a reload and clears itself when the line is removed anywhere on the site. */

  function cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function setAdded(button, on) {
    button.classList.toggle('is-added', on);
    button.disabled = on;
    var label = on ? button.dataset.addedLabel : button.dataset.addLabel;
    if (label && button.hasAttribute('aria-label')) button.setAttribute('aria-label', label);
  }

  function applyCart(cart) {
    var inCart = {};
    (cart && cart.items ? cart.items : []).forEach(function (item) {
      inCart[item.variant_id] = true;
    });

    $$('[data-fq-atc]').forEach(function (button) {
      setAdded(button, inCart[parseInt(button.dataset.variantId, 10)] === true);
    });
  }

  var cartRequest = null;

  function refreshCart(force) {
    if (!cartRequest || force) {
      cartRequest = fetch(cartRoot() + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (response) {
          return response.json();
        })
        .catch(function () {
          return null;
        });
    }
    return cartRequest.then(function (cart) {
      if (cart) applyCart(cart);
      return cart;
    });
  }

  function bindCart(root) {
    var addUrl = root.dataset.cartAddUrl || '/cart/add';

    $$('[data-fq-atc]', root).forEach(function (button) {
      button.addEventListener('click', function (event) {
        event.preventDefault();

        if (button.disabled || button.classList.contains('is-added')) return;

        var id = parseInt(button.dataset.variantId, 10);
        if (!id) return;

        button.setAttribute('aria-busy', 'true');

        fetch(addUrl + '.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/javascript'
          },
          body: JSON.stringify({ items: [{ id: id, quantity: 1 }] })
        })
          .then(function (response) {
            if (!response.ok) throw new Error('add-to-cart failed');
            return response.json();
          })
          .then(function () {
            return refreshCart(true);
          })
          .then(function (cart) {
            document.dispatchEvent(
              new CustomEvent('fndry:cart:updated', {
                detail: { cart: cart, variantId: id, source: 'fndry-quiz' }
              })
            );
          })
          .catch(function () {
            window.location.href = root.dataset.cartUrl || '/cart';
          })
          .finally(function () {
            button.removeAttribute('aria-busy');
          });
      });
    });
  }

  document.addEventListener('fndry:cart:updated', function (event) {
    if (event.detail && event.detail.cart) {
      applyCart(event.detail.cart);
    } else {
      refreshCart(true);
    }
  });

  document.addEventListener('fndry:cart:refresh', function () {
    refreshCart(true);
  });

  window.addEventListener('pageshow', function (event) {
    if (event.persisted) refreshCart(true);
  });

  /* ---------- the flow --------------------------------------------------- */

  function Quiz(root) {
    this.root = root;
    this.steps = $$('[data-fq-step]', root);
    this.result = $('[data-fq-result]', root);
    this.cards = $$('[data-fq-outcome]', root);
    this.countEl = $('[data-fq-count]', root);
    this.fillEl = $('[data-fq-fill]', root);
    this.progressEl = $('[data-fq-progress]', root);
    this.template = root.dataset.progressTemplate || 'Question [current] of [total]';
    this.autoAdvance = root.dataset.autoAdvance === 'true';
    this.fallbackKey = root.dataset.defaultOutcome || 'duo';
    this.index = 0;
    this.pointer = false;
    this.advanceTimer = null;
  }

  Quiz.prototype.total = function () {
    return this.steps.length;
  };

  Quiz.prototype.answerOf = function (step) {
    var checked = $('[data-fq-input]:checked', step);
    return checked ? checked.value : null;
  };

  Quiz.prototype.syncNext = function (step) {
    var next = $('[data-fq-next]', step);
    if (next) next.disabled = this.answerOf(step) === null;
  };

  Quiz.prototype.progress = function () {
    var current = this.index + 1;
    var total = this.total();

    if (this.countEl) {
      this.countEl.textContent = this.template
        .replace('[current]', String(current))
        .replace('[total]', String(total));
    }
    if (this.fillEl) {
      this.fillEl.style.setProperty('--fq-p', total > 0 ? current / total : 1);
    }
  };

  Quiz.prototype.scrollIntoPlace = function (target) {
    if (!target) return;

    var offset = headerOffset();
    var rect = target.getBoundingClientRect();
    // Only move the page when the target isn't comfortably in view already.
    if (rect.top >= offset && rect.bottom <= window.innerHeight) return;

    var top = rect.top + window.pageYOffset - offset;
    if (prefersReducedMotion() || !('scrollBehavior' in document.documentElement.style)) {
      window.scrollTo(0, Math.max(0, top));
    } else {
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }
  };

  Quiz.prototype.show = function (index, moveFocus) {
    var self = this;
    this.index = Math.max(0, Math.min(index, this.total() - 1));

    if (this.result) this.result.hidden = true;
    if (this.progressEl) this.progressEl.hidden = false;

    this.steps.forEach(function (step, i) {
      var on = i === self.index;
      step.hidden = !on;
      step.removeAttribute('data-enter');
      if (on) {
        // reflow so the animation restarts on every step change
        void step.offsetWidth;
        step.setAttribute('data-enter', '');
        self.syncNext(step);
      }
    });

    this.progress();

    var step = this.steps[this.index];
    var heading = $('[data-fq-heading]', step);
    var sr = $('[data-fq-sr]', step);

    if (sr) {
      sr.textContent =
        this.template.replace('[current]', String(this.index + 1)).replace('[total]', String(this.total())) + '. ';
    }

    if (moveFocus && heading) heading.focus({ preventScroll: true });
    this.scrollIntoPlace(step);
  };

  Quiz.prototype.scores = function () {
    var self = this;
    var scores = {};
    this.steps.forEach(function (step) {
      var value = self.answerOf(step);
      if (!value || value === 'none') return;
      var weight = parseInt(step.dataset.weight, 10);
      if (isNaN(weight) || weight < 1) weight = 1;
      scores[value] = (scores[value] || 0) + weight;
    });
    return scores;
  };

  Quiz.prototype.cardFor = function (key) {
    var match = null;
    this.cards.forEach(function (card) {
      if (!match && card.dataset.fqOutcome === key) match = card;
    });
    return match;
  };

  Quiz.prototype.finish = function () {
    var self = this;
    var key = resolve(this.scores(), this.fallbackKey);

    // A resolved key with no outcome block behind it falls back rather than
    // showing an empty result.
    var card = this.cardFor(key) || this.cardFor(this.fallbackKey) || this.cards[0];
    if (!card) return;

    this.steps.forEach(function (step) {
      step.hidden = true;
      step.removeAttribute('data-enter');
    });
    if (this.progressEl) this.progressEl.hidden = true;

    this.cards.forEach(function (item) {
      item.hidden = item !== card;
    });

    if (this.result) {
      this.result.hidden = false;
      // reflow so the card animates in on a repeat run
      void this.result.offsetWidth;
    }

    var heading = $('[data-fq-result-heading]', card);
    if (heading) heading.focus({ preventScroll: true });
    self.scrollIntoPlace(this.result || card);
  };

  Quiz.prototype.restart = function () {
    this.steps.forEach(function (step) {
      $$('[data-fq-input]', step).forEach(function (input) {
        input.checked = false;
      });
    });
    this.cards.forEach(function (card) {
      card.hidden = true;
    });
    this.show(0, true);
  };

  Quiz.prototype.bind = function () {
    var self = this;

    this.steps.forEach(function (step, i) {
      // Pointer selection may auto-advance; keyboard selection must not —
      // arrowing through a radio group changes the checked option on every
      // press, and advancing on that makes the group impossible to use.
      step.addEventListener('pointerdown', function () {
        self.pointer = true;
      });
      step.addEventListener('keydown', function () {
        self.pointer = false;
      });

      step.addEventListener('change', function (event) {
        if (!event.target.matches('[data-fq-input]')) return;
        self.syncNext(step);

        window.clearTimeout(self.advanceTimer);
        if (!self.autoAdvance || !self.pointer) return;

        self.advanceTimer = window.setTimeout(function () {
          self.advance();
        }, 320);
      });

      // Enter on a radio moves on, the way it would submit a form.
      step.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter') return;
        if (!event.target.matches('[data-fq-input]')) return;
        event.preventDefault();
        if (self.answerOf(step) !== null) self.advance();
      });

      var next = $('[data-fq-next]', step);
      if (next) {
        next.addEventListener('click', function () {
          window.clearTimeout(self.advanceTimer);
          self.advance();
        });
      }

      var back = $('[data-fq-back]', step);
      if (back) {
        back.addEventListener('click', function () {
          window.clearTimeout(self.advanceTimer);
          self.show(i - 1, true);
        });
      }
    });

    var restart = $('[data-fq-restart]', this.root);
    if (restart) {
      restart.addEventListener('click', function () {
        self.restart();
      });
    }
  };

  Quiz.prototype.advance = function () {
    if (this.answerOf(this.steps[this.index]) === null) return;
    if (this.index >= this.total() - 1) {
      this.finish();
    } else {
      this.show(this.index + 1, true);
    }
  };

  /* ---------- init ------------------------------------------------------ */

  function init(root) {
    if (!root || root.dataset.fqReady === 'true') return;

    try {
      var quiz = new Quiz(root);
      if (quiz.total() === 0) return; // nothing to drive — leave the fallback up

      root.removeAttribute('data-fq-failed');
      root.dataset.fqReady = 'true';

      quiz.bind();
      bindCart(root);
      refreshCart(false);
      quiz.show(0, false);

      root._fndryQuiz = quiz;
    } catch (error) {
      root.removeAttribute('data-fq-ready');
      delete root.dataset.fqReady;
      root.setAttribute('data-fq-failed', '');
    }
  }

  function initAll(scope) {
    $$(ROOT, scope || document).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll();
    });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  /* Selecting a block in the theme editor previews it: a question block jumps
     to that step, an outcome block shows that recommendation. */
  document.addEventListener('shopify:block:select', function (event) {
    var root = event.target.closest ? event.target.closest(ROOT) : null;
    if (!root || !root._fndryQuiz) return;
    var quiz = root._fndryQuiz;

    var step = event.target.closest('[data-fq-step]');
    if (step) {
      quiz.show(quiz.steps.indexOf(step), false);
      return;
    }

    var card = event.target.closest('[data-fq-outcome]');
    if (card) {
      quiz.steps.forEach(function (item) {
        item.hidden = true;
      });
      if (quiz.progressEl) quiz.progressEl.hidden = true;
      quiz.cards.forEach(function (item) {
        item.hidden = item !== card;
      });
      if (quiz.result) quiz.result.hidden = false;
    }
  });
})();
